/**
 * How far back the camera stands so the whole aquarium is on screen, on any
 * screen: a wide monitor, a laptop, or a phone held upright.
 *
 * Pure arithmetic, so it is tested in node (`tests/fit.test.ts`). The
 * aquarium is treated as its bounding sphere; the sphere fits when the camera
 * stands at `radius / sin(halfAngle)` from its center, where `halfAngle` is the
 * narrower of the frustum's two half-angles. On a landscape screen that is the
 * vertical one; upright on a phone it is the horizontal one, which is what a
 * fixed distance got wrong (M8: the aquarium ran off both sides of a phone).
 */

/** The vertical field of view, degrees: wider on a portrait screen, so the aquarium is not tiny. */
export function fovFor(aspect: number): number {
  return aspect < 1 ? 50 : 40;
}

/**
 * The exhibit's vertical field of view, degrees (M94, the brief's "low-perspective camera"): narrower than the
 * map's, so the tank reads as a display tank, its far end hardly smaller than its near; wider on a portrait
 * screen, as the map's is (JUDGED both).
 */
export function exhibitFovFor(aspect: number): number {
  return aspect < 1 ? 34 : 24;
}

/** What the HUD leaves of a canvas for the aquarium, and the larger virtual frame that centers the camera on it. */
export interface VisibleRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  /** The visible rectangle's width and height, pixels. */
  width: number;
  height: number;
  /** The virtual frame the camera renders, its middle the visible rectangle's middle, and where the canvas lies in it. */
  fullWidth: number;
  fullHeight: number;
  offsetX: number;
  offsetY: number;
}

/**
 * The rectangle a `width` by `height` canvas shows the aquarium in, between the HUD's insets, each held so some of
 * the canvas stays (JUDGED): a right-hand panel to 65% of the width, both side panels together to 75% (M94's
 * review: at 65% a window 1,101 to 1,316 px wide framed up to 89 px of the habitat under the left panel), the top
 * to 30% of the height, the top and the bottom together to 65%. The camera renders a virtual frame centered on it,
 * and the canvas is the part of that frame it covers.
 */
export function visibleRect(width: number, height: number, insets: { top: number; right: number; bottom: number; left?: number }): VisibleRect {
  const right = Math.min(insets.right, width * 0.65);
  const left = Math.min(insets.left ?? 0, width * 0.75 - right);
  const top = Math.min(insets.top, height * 0.3);
  const bottom = Math.min(insets.bottom, height * 0.65 - top);
  return {
    left, right, top, bottom,
    width: width - right - left,
    height: height - top - bottom,
    fullWidth: width + Math.abs(right - left),
    fullHeight: height + Math.abs(top - bottom),
    offsetX: right > left ? right - left : 0,
    offsetY: bottom > top ? bottom - top : 0,
  };
}

/** A smooth start and end over 0 to 1: the cubic's ease in and out. */
export function easeInOut(t: number): number {
  const s = Math.min(1, Math.max(0, t));
  return s < 0.5 ? 4 * s * s * s : 1 - (-2 * s + 2) ** 3 / 2;
}

/** Distance from the aquarium's center at which a sphere of `radius` fits the view, with a margin. */
export function fitDistance(radius: number, aspect: number, fovDeg: number, margin = 1.08): number {
  const vHalf = (fovDeg * Math.PI) / 360;
  const hHalf = Math.atan(Math.tan(vHalf) * aspect);
  return (radius * margin) / Math.sin(Math.min(vHalf, hHalf));
}

/**
 * Whether a sphere of `radius` at distance `distance` lies inside the view.
 * The test's own check, independent of `fitDistance`'s formula: the sphere is
 * inside when its angular radius is no more than either half-angle.
 */
export function sphereFits(radius: number, distance: number, aspect: number, fovDeg: number): boolean {
  const angular = Math.asin(Math.min(1, radius / distance));
  const vHalf = (fovDeg * Math.PI) / 360;
  const hHalf = Math.atan(Math.tan(vHalf) * aspect);
  return angular <= vHalf + 1e-12 && angular <= hHalf + 1e-12;
}

/**
 * The nearest the camera comes to the point it turns about, scene units (M81, the user's
 * word: "the zoom is not able to magnify enough"). At 2 the view is 1.46 units high
 * (2 × 2 tan 20 degrees, the landscape field of view's half-angle; M81's reviewer: this read 2 tan 20), an eighth of the aquarium's 12-unit
 * side and 62 of its skin's texels (`aquariumView.ts`), each about 14 pixels on an
 * 860-pixel-high view. It was 8, where the view still held half the box.
 */
export const NEAREST = 2;

type Vec3 = readonly [number, number, number];

/**
 * Where the camera should turn about before a zoom toward `hit`, the point under the
 * pointer: on the camera's line of sight (`forward`, a unit vector), as deep along it as
 * `hit` is, so the picture does not move and the zoom closes in on `hit`; or null when
 * `hit` is no farther along the line than `nearest`, or farther than `farthest` (the
 * controls' farthest: M81's reviewer, a pivot past it had the controls pull the camera
 * forward to it, 50 units at a notch, and zooming out zoom in), where the pivot is left as
 * it is.
 */
export function pivotAt(camera: Vec3, forward: Vec3, hit: Vec3, nearest: number, farthest: number): [number, number, number] | null {
  const depth = (hit[0] - camera[0]) * forward[0] + (hit[1] - camera[1]) * forward[1] + (hit[2] - camera[2]) * forward[2];
  if (!(depth > nearest) || depth > farthest) return null;
  return [camera[0] + forward[0] * depth, camera[1] + forward[1] * depth, camera[2] + forward[2] * depth];
}

/**
 * How far from `target`, along `direction` (toward the camera, any length), the camera stands so that
 * `points` just fit the view, the nearest of them at the edge (M86, the user's word, of the diorama: "the
 * aquarium should be bigger as well. it should fill the screen at least"): no margin, and no sphere
 * around them, so a box fills the view's tighter side; or, `heightOnly`, its height whatever its width (M105: the
 * whole tank as a panorama on a narrow screen). Found by halving between a distance that fits and one that does not.
 */
export function fillDistance(points: readonly Vec3[], target: Vec3, direction: Vec3, aspect: number, fovDeg: number, heightOnly = false): number {
  const len = Math.hypot(direction[0], direction[1], direction[2]);
  const back = direction.map((v) => v / len) as unknown as Vec3;
  // The camera's axes: looking along -back, its up as near the world's as it can be.
  const fwd: Vec3 = [-back[0], -back[1], -back[2]];
  // Right: forward crossed with the world's up, (0, 1, 0).
  const rightRaw: Vec3 = [-fwd[2], 0, fwd[0]];
  const rl = Math.hypot(...rightRaw) || 1;
  const right: Vec3 = [rightRaw[0] / rl, rightRaw[1] / rl, rightRaw[2] / rl];
  const up: Vec3 = [right[1] * fwd[2] - right[2] * fwd[1], right[2] * fwd[0] - right[0] * fwd[2], right[0] * fwd[1] - right[1] * fwd[0]];
  const tanV = Math.tan((fovDeg * Math.PI) / 360);
  const tanH = tanV * aspect;
  const fits = (d: number): boolean => points.every((p) => {
    const rel: Vec3 = [p[0] - target[0] - back[0] * d, p[1] - target[1] - back[1] * d, p[2] - target[2] - back[2] * d];
    const z = rel[0] * fwd[0] + rel[1] * fwd[1] + rel[2] * fwd[2];
    if (z <= 0) return false;
    const x = rel[0] * right[0] + rel[1] * right[1] + rel[2] * right[2];
    const y = rel[0] * up[0] + rel[1] * up[1] + rel[2] * up[2];
    return (heightOnly || Math.abs(x) <= z * tanH) && Math.abs(y) <= z * tanV;
  });
  let near = 0;
  let far = 1;
  while (!fits(far) && far < 1e6) far *= 2;
  for (let i = 0; i < 50; i++) {
    const mid = (near + far) / 2;
    if (fits(mid)) far = mid;
    else near = mid;
  }
  return far;
}

/**
 * How far from its target a frame stands the camera, along `direction` (toward the camera, any length), on a view of
 * this shape (M105): a habitat's shot back until its half-height fills the view at the target, whatever the shape; a
 * panorama (`tall`) until its points' height fills it, but never more than `tall` times closer than filling it with
 * all of them; any other frame until all its points fill it, a little more (the scene's rule, `scene.ts`).
 */
export function frameDistance(frame: { target: Vec3; points: readonly Vec3[]; half?: number; tall?: number }, direction: Vec3, aspect: number, fovDeg: number): number {
  if (frame.half !== undefined) return frame.half / Math.tan((fovDeg * Math.PI) / 360);
  const fill = (heightOnly: boolean): number => 1.04 * fillDistance(frame.points, frame.target, direction, aspect, fovDeg, heightOnly);
  return frame.tall !== undefined ? Math.max(fill(true), fill(false) / frame.tall) : fill(false);
}

